import groq from 'groq';
import { type FC, useCallback } from 'react';

import { CATEGORY_HAS_ARTICLES_PROJECTION_FIELD } from '../../../lib/sanity/commonGroqs';
import type { Category } from '../../../types/cmsTypes';
import { getLibraryPageUrl } from '../../../utils/routing';

import { DropdownNavItem, NavItem } from './NavItems';

export const LIBRARY_NAV_CATEGORY_QUERY: string = groq`*[_type == "category"] {
    title,
    slug,
    ${CATEGORY_HAS_ARTICLES_PROJECTION_FIELD},
    goal->{ title, slug },
} [hasArticles] { title, slug, goal } | order(title asc)`;

export interface LibraryNavGoal {
  title: string;
  slug: {
    current: string;
  };
}

export type LibraryNavCategory = Pick<Category, 'title' | 'slug' | 'goal'> & {
  goal: LibraryNavGoal;
};

export interface LibraryNavGoalSection {
  goal: LibraryNavGoal;
  categories: LibraryNavCategory[];
}

export enum NonCategorySlugs {
  HOME = '',
  STORIES = 'stories',
  COACH = 'coach',
}

interface Props {
  activeSlug: string | NonCategorySlugs;
  categoriesByGoal: LibraryNavGoalSection[];
}

const LibraryNav: FC<Props> = ({ activeSlug, categoriesByGoal }) => {
  const renderNonDropdownItem = useCallback(
    (label: string, slug: NonCategorySlugs) => (
      <NavItem
        label={label}
        href={getLibraryPageUrl({ slug })}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        isActive={activeSlug === slug}
      />
    ),
    [activeSlug],
  );

  return (
    <nav className="overflow-visible overflow-x-auto">
      <div className="flex items-center space-x-8 px-1 md:space-x-12">
        {renderNonDropdownItem('Home', NonCategorySlugs.HOME)}

        {categoriesByGoal.map(({ goal, categories }) => (
          <DropdownNavItem
            key={goal.slug.current}
            label={goal.title}
            items={categories.map((c) => ({
              label: c.title ?? '',
              href: getLibraryPageUrl({ slug: c.slug?.current }),
              isActive: activeSlug === c.slug?.current,
            }))}
          />
        ))}

        {renderNonDropdownItem('Stories', NonCategorySlugs.STORIES)}

        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison */}
        {activeSlug === NonCategorySlugs.COACH
          ? renderNonDropdownItem('For Coaches', NonCategorySlugs.COACH)
          : null}
      </div>
    </nav>
  );
};

export default LibraryNav;
